import React from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// The big round record control. Rings pulse out while recording and the orb
// swells with the live input level, so the user can see the mic is hearing them.
export const RecordOrb = ({ recording, processing, level = 0, progress = null, onClick, disabled }) => {
  const size = 104;
  const r = 50;
  const c = 2 * Math.PI * r;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || processing}
      aria-label={recording ? 'Stop recording' : 'Start recording'}
      className="group relative grid place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-4 focus-visible:ring-offset-ink-950 disabled:opacity-60"
      style={{ width: size, height: size }}
    >
      {recording && (
        <>
          <span className="absolute inset-2 rounded-full bg-coral-500/40 animate-ring-out" />
          <span className="absolute inset-2 rounded-full bg-coral-500/30 animate-ring-out [animation-delay:0.9s]" />
        </>
      )}
      {progress != null && (
        <svg className="absolute inset-0 -rotate-90" width={size} height={size} aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="none" />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke="url(#cz-orb-g)" strokeWidth="3" fill="none" strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - progress)}
            style={{ transition: 'stroke-dashoffset 0.3s linear' }}
          />
          <defs>
            <linearGradient id="cz-orb-g" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#3387FF" />
              <stop offset="1" stopColor="#5EE0FF" />
            </linearGradient>
          </defs>
        </svg>
      )}
      <span
        className={`relative grid h-[84px] w-[84px] place-items-center rounded-full transition-[transform,background-color,box-shadow] duration-150 ${
          recording
            ? 'bg-white text-ink-950'
            : 'bg-gradient-to-br from-coral-400 to-coral-600 text-white shadow-[0_10px_40px_-8px_rgba(255,107,74,0.7)] group-hover:scale-105'
        }`}
        style={recording ? { transform: `scale(${1 + level * 0.18})` } : undefined}
      >
        {processing ? (
          <Loader2 className="h-8 w-8 animate-spin" />
        ) : recording ? (
          <Square className="h-7 w-7 fill-current" />
        ) : (
          <Mic className="h-9 w-9" />
        )}
      </span>
    </button>
  );
};

// Canvas with the live melody line on top, record control and timer below.
const RecordStage = ({ contour, recorder, targetSeconds, hint, className = '' }) => {
  const { status, seconds } = recorder;
  const recording = status === 'recording';
  return (
    <div className={`cz-card relative overflow-hidden ${className}`}>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-full bg-[radial-gradient(40rem_14rem_at_50%_0%,rgba(51,135,255,0.10),transparent_70%)]" />
      <canvas ref={contour.canvasRef} className="relative block h-36 w-full sm:h-48" aria-hidden="true" />
      <div className="relative -mt-3 flex flex-col items-center gap-3 px-6 pb-7">
        <RecordOrb
          recording={recording}
          processing={status === 'processing'}
          level={contour.readout.level}
          progress={targetSeconds ? Math.min(1, seconds / targetSeconds) : null}
          onClick={recording ? recorder.stop : recorder.start}
        />
        <div className="text-center">
          <p className="font-mono text-2xl font-medium tabular-nums text-white" aria-live="off">{fmt(seconds)}</p>
          <p className="mt-1 min-h-[1.25rem] text-sm text-white/55" aria-live="polite">{hint}</p>
        </div>
      </div>
    </div>
  );
};

export default RecordStage;
